import request from "@/requests/request";

export const getUser = ({ signal }: IGenericHookOptions): Promise<IGetUserRS> =>
  request({
    url: "/v1/auth-status",
    signal,
  }).then(res => res.data);

export const postLogin = (data: IPostLoginRQ): Promise<IGetUserRS> =>
  request({
    method: "POST",
    url: "/v1/auth/login",
    body: data,
  }).then(res => res.data);

export const postLogout = () =>
  request({
    url: "/v1/auth/logout",
    method: "POST",
  });

export const postResetPassword = (data: IPostPasswordResetPQ) =>
  request({
    url: "/v1/auth/reset-password",
    method: "POST",
    body: data,
  });

export const getRelatedCustomers = ({
  signal,
}: IGenericHookOptions): Promise<IGetRelatedCustomersRS> =>
  request({
    url: "/v1/team-member/parent-customer",
    method: "GET",
    signal,
  }).then(res => res.data);

export const postJwtForAccount = async ({
  merchant_id,
  integration,
}: IPostJwtForAccountRQ): Promise<IPostJwtForAccountRS> =>
  request({
    url: `/v1/auth/refresh-token${!!integration ? integration : ""}`,
    method: "POST",
    body: {
      merchantId: merchant_id,
    },
  }).then(res => res.data);

export const postResendVerificationEmail = (
  data: IPostResendVerificationEmailRQ,
) =>
  request({
    url: "/v1/signup/resend-email",
    method: "POST",
    body: data,
  });

export const postForgotPassword = (data: IPostForgotPasswordRQ) =>
  request({
    url: "/v1/auth/forgot-password",
    method: "POST",
    body: data,
  });

export const postConfirmVerificationToken = async (data: IVerifyEmailRQ) =>
  request({
    url: "/v1/signup/verify-email",
    method: "POST",
    body: data,
  });

export const postConfirmEmailVerificationExisting = async (
  data: IVerifyEmailExistingRQ,
) =>
  request({
    url: "/v1/signup/existing",
    method: "POST",
    body: data,
  }).then(res => res.data);

export const postResendEmailVerificationExisting = async (
  data: IResendVerifyEmailExistingRQ,
) =>
  request({
    url: "/v1/signup/existing/resend",
    method: "POST",
    body: data,
  }).then(res => res.data);

export const postConfirmVerificationTokenExistingEmail = async (
  data: IVerifyExistingEmailCodeRQ,
) =>
  request({
    url: "/v1/signup/existing/verify-email",
    method: "POST",
    body: data,
  }).then(res => res.data);

export const postSignup = async ({
  data,
  integration,
}: {
  data: IPostSignupRQ;
  integration?: string;
}): Promise<IPostSignUpRS> => {
  const url = integration ? `/v2/signup${integration}` : `/v2/signup`;

  return request({
    url: url,
    method: "POST",
    body: data,
  }).then(res => res.data);
};

export const postChangePassword = (data: IPostChangePasswordPQ) =>
  request({
    url: "/v1/user/change-password",
    method: "POST",
    body: data,
  });

export const getBilling = async ({
  signal,
}: IGenericHookOptions): Promise<IGetBillingRS> =>
  await request({
    url: "/v1/billing/overview",
    signal,
  }).then(res => res.data);

export const cancelSubscription = async ({
  id,
  version,
}: ICancelSubscriptionRQ): Promise<IGetBillingRS> =>
  await request({
    url: `/v1/subscription/${id}/cancel`,
    method: "POST",
    body: { version },
  }).then(res => res.data);

export const restartSubscription = async (
  product: ISubscriptionProduct,
): Promise<void> =>
  await request({
    url: `/v1/subscription/re-subscribe`,
    method: "POST",
    body: { product },
  }).then(res => res.data);

export const reprocessSubscription = async (
  subscriptionId: number,
): Promise<void> =>
  await request({
    url: `/v1/subscription/${subscriptionId}/reprocess`,
    method: "POST",
  }).then(res => res.data);

export const postJitaLogout = async (): Promise<void> =>
  await request({
    url: "/v1/auth/jita-logout",
    method: "POST",
  }).then(res => res.data);

export const postAddCustomer = async (
  bodyParams: IAddCustomerRequest,
): Promise<IAddCustomerRS> =>
  await request({
    url: "/v1/customer/new",
    method: "POST",
    body: bodyParams,
  }).then(res => res.data);

export const getCompanyIndustry = async (): Promise<ICoreGetCompanyIndustry> =>
  await request({
    url: "/v1/static-data/company-industry",
  }).then(res => res.data);

export const getTeamMembers = async ({
  params,
  signal,
}: {
  params: IGetTeamMemberParams;
  signal: IGenericHookOptions["signal"];
}): Promise<IGetTeamMembersRS> =>
  await request({
    url: "/v1/team-member",
    params,
    signal,
  }).then(res => res.data);

export const postAddTeamMember = async (
  body: IAddTeamMemberRQ,
): Promise<void> =>
  await request({
    url: "/v1/team-member/add",
    method: "POST",
    body,
  }).then(res => res.data);

export const postUpdateTeamMember = async (
  body: IAddTeamMemberRQ,
): Promise<void> =>
  await request({
    url: "/v1/team-member/update",
    method: "POST",
    body,
  }).then(res => res.data);

export const postDeleteTeamMember = async (
  body: IDeleteTeamMemberRQ,
): Promise<void> =>
  await request({
    url: "/v1/team-member/delete",
    method: "POST",
    body,
  }).then(res => res.data);

export const postResendInviteTeamMember = async (
  body: IResendInviteTeamMemberRQ,
): Promise<void> =>
  await request({
    url: "/v1/team-member/resend-invite",
    method: "POST",
    body,
  }).then(res => res.data);

export const getCustomerSetupChecklist = async ({
  signal,
}: IGenericHookOptions): Promise<IGetCustomerSetupChecklistRS> =>
  await request({
    url: "/v1/signup/customer-setup-checklist",
    signal,
  }).then(res => res.data);
